import { forwardRef, useState, type ChangeEvent, type ClipboardEvent, type CSSProperties, type FocusEvent, type FormEvent, type KeyboardEvent, type MouseEvent, type ReactNode, type Ref } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// Thin wrapper mapping antd <Input> (+ Input.Search/.TextArea/.Password) onto
// MUI TextField so call sites swap by import only. antd onChange(e) matches MUI;
// onPressEnter -> onKeyDown Enter; prefix/suffix -> InputAdornment.

type Size = "small" | "middle" | "large";
const muiSize = (s?: Size) => (s === "large" ? "medium" : "small");

export interface InputProps {
  value?: string | number;
  defaultValue?: string | number;
  // Method-signature form so narrower call-site handlers (e.g. ChangeEvent<HTMLInputElement>)
  // are accepted (TS checks method params bivariantly).
  onChange?(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  onPressEnter?: (e: KeyboardEvent) => void;
  showCount?: boolean;
  onPasteCapture?: (e: ClipboardEvent<HTMLElement>) => void;
  "aria-label"?: string;
  "data-testid"?: string;
  onInput?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  onKeyUp?: (e: KeyboardEvent) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClick?: (e: MouseEvent) => void;
  placeholder?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  addonBefore?: ReactNode;
  addonAfter?: ReactNode;
  allowClear?: boolean;
  maxLength?: number;
  disabled?: boolean;
  size?: Size;
  type?: string;
  name?: string;
  status?: "error" | "warning" | "" | false;
  style?: CSSProperties;
  className?: string;
  id?: string;
  autoFocus?: boolean;
  "data-cy"?: string;
}

const InputBase = forwardRef(({
  value, defaultValue, onChange, onPressEnter, placeholder, prefix, suffix,
  addonBefore, addonAfter, maxLength, disabled, size, type, style, className, autoFocus, status,
  // data-*/aria-label go on the <input> (matching antd) so `input[data-cy=x]` selectors work.
  "data-cy": dataCy, "data-testid": dataTestid, "aria-label": ariaLabel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  allowClear, onKeyDown, showCount, ...rest
}: InputProps, ref: Ref<HTMLInputElement>) => {
  const start = prefix ?? addonBefore;
  const end = suffix ?? addonAfter;
  // antd showCount renders a live "n/max" counter below the field; MUI has no
  // built-in, so surface it via helperText. Track length for uncontrolled use;
  // a controlled `value` always wins.
  const [count, setCount] = useState(String(defaultValue ?? "").length);
  const len = value != null ? String(value).length : count;
  return (
    <TextField
      inputRef={ref}
      value={value}
      defaultValue={defaultValue}
      error={status === "error"}
      onChange={(e) => {
        if (showCount) setCount(e.target.value.length);
        onChange?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") onPressEnter?.(e);
        onKeyDown?.(e);
      }}
      placeholder={placeholder}
      disabled={disabled}
      size={muiSize(size)}
      type={type}
      autoFocus={autoFocus}
      // antd Input defaults to width:100%; MUI TextField is content-width. Fill the
      // container (see AutoComplete.tsx). An explicit style.width (inline) still wins.
      fullWidth
      style={style}
      className={className}
      helperText={showCount ? (maxLength != null ? `${len}/${maxLength}` : `${len}`) : undefined}
      FormHelperTextProps={showCount ? { sx: { textAlign: "right", mx: 0 } } : undefined}
      InputProps={{
        startAdornment: start ? <InputAdornment position="start">{start}</InputAdornment> : undefined,
        endAdornment: end ? <InputAdornment position="end">{end}</InputAdornment> : undefined,
      }}
      inputProps={{ maxLength, "data-cy": dataCy, "data-testid": dataTestid, "aria-label": ariaLabel }}
      {...rest}
    />
  );
});
InputBase.displayName = "Input";

export interface SearchProps extends Omit<InputProps, "onChange"> {
  onSearch?: (value: string) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  enterButton?: boolean | ReactNode;
  loading?: boolean;
}

const Search = ({ onSearch, onChange, value, placeholder, size, style, className, disabled, id, autoFocus, ...rest }: SearchProps) => {
  const [internal, setInternal] = useState("");
  const current = value !== undefined ? String(value) : internal;
  const dataCy = (rest as Record<string, unknown>)["data-cy"] as string | undefined;
  const dataTestid = (rest as Record<string, unknown>)["data-testid"] as string | undefined;
  return (
    <TextField
      id={id}
      autoFocus={autoFocus}
      // antd Input.Search renders <input type="search"> — match it for input[type=search] selectors.
      type="search"
      value={current}
      onChange={(e) => {
        if (value === undefined) setInternal(e.target.value);
        onChange?.(e);
      }}
      onKeyDown={(e) => e.key === "Enter" && onSearch?.(current)}
      placeholder={placeholder}
      disabled={disabled}
      size={muiSize(size)}
      fullWidth
      style={style}
      className={className}
      data-cy={dataCy}
      data-testid={dataTestid}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton aria-label="search" onClick={() => onSearch?.(current)} edge="end" size="small">
              <SearchIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

const TextArea = ({
  value, defaultValue, onChange, placeholder, disabled, size, style, className,
  rows, autoSize,
  "data-cy": dataCy, "data-testid": dataTestid, "aria-label": ariaLabel,
  ...rest
}: InputProps & { rows?: number; autoSize?: { minRows?: number; maxRows?: number } | boolean }) => {
  const auto = typeof autoSize === "object" ? autoSize : undefined;
  return (
    <TextField
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      size={muiSize(size)}
      multiline
      minRows={auto?.minRows ?? rows ?? 3}
      maxRows={auto?.maxRows}
      fullWidth
      style={style}
      className={className}
      // data-*/aria-label land on the <textarea> (matching antd) so
      // `textarea[data-cy="chat-input"]` selectors resolve.
      inputProps={{ "data-cy": dataCy, "data-testid": dataTestid, "aria-label": ariaLabel }}
      {...(rest as Record<string, unknown>)}
    />
  );
};

const Password = ({ value, onChange, placeholder, disabled, size, style, className, id, autoFocus }: InputProps) => {
  const [show, setShow] = useState(false);
  return (
    <TextField
      id={id}
      autoFocus={autoFocus}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      size={muiSize(size)}
      type={show ? "text" : "password"}
      fullWidth
      style={style}
      className={className}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton aria-label="toggle password" onClick={() => setShow((s) => !s)} edge="end" size="small">
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

// Attach antd's Input.Search/.TextArea/.Password statics (Object.assign keeps
// the forwardRef type + statics typed for consumers).
export const Input = Object.assign(InputBase, { Search, TextArea, Password });

export default Input;
